
import { Contact } from '@/types/contact';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ContactListItem } from './ContactListItem';
import { LoadingState } from './LoadingState';
import { Checkbox } from '@/components/ui/checkbox';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { setSelectedContacts } from '@/store/slices/contacts/contactsSlice';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tag as TagIcon, Users, Mail, Building, Shield, Activity, Clock, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ImportExportActions } from './ImportExportActions';
import { ContactListPagination } from './ContactListPagination';
import { useState } from 'react';
import { bulkUpdateCustomers } from '@/store/slices/contacts/contactsSlice';
import { TagSelectorDialog } from './TagSelectorDialog';

interface ContactListProps {
  contacts: Contact[];
  loading: boolean;
}

export const ContactList = ({ contacts, loading }: ContactListProps) => {
  const dispatch = useAppDispatch();
  const selectedContacts = useAppSelector(state => state.contacts.selectedContacts);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);

  const handleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      dispatch(setSelectedContacts([]));
    } else {
      dispatch(setSelectedContacts(contacts.map(contact => contact.id)));
    }
  };

  const handleQuickTag = () => {
    setIsTagDialogOpen(true);
  };

  const handleMarkAsArchived = () => {
    dispatch(bulkUpdateCustomers({
      customerIds: selectedContacts,
      archived: true,
    }));
  };

  // Calculate pagination values
  const totalPages = Math.ceil(contacts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedContacts = contacts.slice(startIndex, endIndex);

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-slate-200/60 shadow-lg shadow-slate-900/5 overflow-hidden">
      {/* Table Header Controls */}
      <div className="p-6 border-b border-slate-200/60 bg-gradient-to-r from-slate-50/80 via-white to-slate-50/80">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedContacts.length === contacts.length && contacts.length > 0}
                onCheckedChange={handleSelectAll}
                className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
              />
              <span className="text-sm font-medium text-slate-700">
                {selectedContacts.length > 0 ? (
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    {selectedContacts.length} selected
                  </span>
                ) : (
                  `${contacts.length} contacts`
                )}
              </span>
            </div>
            {/* This space is now empty as per the new design */}
          </div>
          {selectedContacts.length > 0 ? (
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleMarkAsArchived}
                      className="h-8 px-3 bg-blue-50/80 border-blue-200/60 text-blue-700 hover:bg-blue-100/80 transition-all duration-200"
                    >
                      <Archive className="h-3 w-3 mr-1.5" />
                      Mark as archived
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Archive selected contacts</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleQuickTag}
                      className="h-8 px-3 bg-blue-50/80 border-blue-200/60 text-blue-700 hover:bg-blue-100/80 transition-all duration-200"
                    >
                      <TagIcon className="h-3 w-3 mr-1.5" />
                      Add Tags
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Quickly assign tags to selected contacts</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <ImportExportActions contacts={contacts} />
            </div>
          ) : (
            <ImportExportActions contacts={contacts} />
          )}
        </div>
      </div>

      {/* Enhanced Table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-slate-200/60 bg-gradient-to-r from-slate-50/50 via-slate-25/50 to-slate-50/50 hover:bg-slate-50/70">
              <TableHead className="w-12 py-4">
                <Checkbox
                  checked={selectedContacts.length === contacts.length && contacts.length > 0}
                  onCheckedChange={handleSelectAll}
                  className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                />
              </TableHead>
              <TableHead className="py-4 font-semibold text-slate-700">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-slate-500" />
                  Name
                </div>
              </TableHead>
              <TableHead className="py-4 font-semibold text-slate-700">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-500" />
                  Email
                </div>
              </TableHead>
              <TableHead className="py-4 font-semibold text-slate-700">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-slate-500" />
                  Company
                </div>
              </TableHead>
              <TableHead className="py-4 font-semibold text-slate-700">
                <div className="flex items-center gap-2">
                  <TagIcon className="h-4 w-4 text-slate-500" />
                  Type
                </div>
              </TableHead>
              <TableHead className="py-4 font-semibold text-slate-700">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-slate-500" />
                  Status
                </div>
              </TableHead>
              <TableHead className="py-4 font-semibold text-slate-700">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-500" />
                  Last Contacted
                </div>
              </TableHead>
              <TableHead className="py-4 font-semibold text-slate-700">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-slate-500" />
                  Activity
                </div>
              </TableHead>
              <TableHead className="text-right py-4 font-semibold text-slate-700">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedContacts.map((contact, index) => (
              <ContactListItem
                key={contact.id}
                contact={contact}
                isSelected={selectedContacts.includes(contact.id)}
                index={index}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Empty State */}
      {contacts.length === 0 && !loading && (
        <div className="p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center">
            <Users className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">No contacts found</h3>
          <p className="text-slate-600 mb-4">Get started by adding your first contact.</p>
        </div>
      )}

      {/* Pagination */}
      {contacts.length > 0 && (
        <div className="border-t border-slate-200/60 bg-gradient-to-r from-slate-50/30 via-white to-slate-50/30">
          <ContactListPagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={contacts.length}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      )}
      <TagSelectorDialog
        open={isTagDialogOpen}
        onOpenChange={setIsTagDialogOpen}
        selectedContacts={selectedContacts}
      />
    </div>
  );
};
